import { useState } from 'react';
import { ActionIcon, Badge, Button, Divider, Group, Modal, Select, Stack, Text, Tooltip } from '@mantine/core';
import { IconFaceId, IconLink, IconLinkOff, IconUserPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

/**
 * XODIM ↔ FACE ID YOZUVI — Xodimlar va O'qituvchilar ro'yxatidagi ustun.
 *
 * FACE ID sahifasida bog'lash QURILMA tomonidan (qurilmadagi odam → xodim)
 * qilinadi. Bu yerda esa TESKARI yo'nalish: admin xodimlar ro'yxatida turib,
 * kim terminalga bog'lanmaganini ko'radi va shu joyning o'zida bog'laydi.
 * Ikkalasi bitta endpointga yozadi (`PATCH /faceid/people/:id`).
 *
 * Ro'yxat sahifasi har qatorda alohida so'rov yubormasligi uchun xarita BITTA
 * so'rov bilan olinadi (`useFaceLinks`) va qatorlarga uzatiladi.
 */

export interface FaceLink {
  personId: string;
  terminalId: string;
  userId: string;
  employeeNo: string;
  name: string;
  hasFace: boolean;
  /** false — yozuv paneldan yaratilgan, agent uni hali qurilmaga yozmagan. */
  synced: boolean;
}

interface UnlinkedPerson {
  id: string;
  employeeNo: string;
  name: string;
  hasFace: boolean;
  terminal: { id: string; name: string };
}

const xato = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

/** Xodim id → yozuv. Terminal yo'q yoki ruxsat bo'lmasa `enabled: false` — ustun yashiriladi. */
export function useFaceLinks() {
  const q = useQuery({
    queryKey: ['faceid-links'],
    queryFn: async () => (await api.get<{ terminals: number; links: FaceLink[] }>('/faceid/links')).data,
    retry: false,
    staleTime: 30_000,
  });
  const map: Record<string, FaceLink> = {};
  for (const l of q.data?.links ?? []) map[l.userId] = l;
  return { enabled: (q.data?.terminals ?? 0) > 0, map };
}

/** Jadval katakchasi: holat nishoni + bog'lash oynasini ochuvchi tugma. */
export function FaceIdCell({ user, link }: { user: { id: string; fullName: string; isActive?: boolean }; link?: FaceLink }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    // Qator bosilganda xodim sahifasiga o'tiladi — katakchadagi bosish unga yetmasin.
    <Group gap={6} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
      {link ? (
        <Tooltip label={link.synced ? link.name : t('faceLink.pendingHint')} withArrow>
          <Badge color={link.synced ? 'teal' : 'orange'} variant="light" ff="monospace">
            №{link.employeeNo}{link.synced ? '' : ' ⏳'}
          </Badge>
        </Tooltip>
      ) : (
        <Badge color="gray" variant="light">{t('faceLink.none')}</Badge>
      )}
      <Tooltip label={t('faceLink.manage')} withArrow>
        <ActionIcon variant="subtle" size="sm" onClick={() => setOpen(true)} aria-label={t('faceLink.manage')}>
          <IconFaceId size={16} />
        </ActionIcon>
      </Tooltip>
      <Modal opened={open} onClose={() => setOpen(false)} title={t('faceLink.title')} centered>
        {open && <FaceLinkForm user={user} link={link} onDone={() => setOpen(false)} />}
      </Modal>
    </Group>
  );
}

function FaceLinkForm({ user, link, onDone }: { user: { id: string; fullName: string; isActive?: boolean }; link?: FaceLink; onDone: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tanlov, setTanlov] = useState<string | null>(null);

  const { data: bosh = [], isLoading } = useQuery({
    queryKey: ['faceid-unlinked'],
    queryFn: async () => (await api.get<UnlinkedPerson[]>('/faceid/people/unlinked')).data,
    enabled: !link,
  });

  const yangila = () => {
    ['faceid-links', 'faceid-unlinked', 'faceid-people'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };
  const xatoBildir = (e: unknown) => notifications.show({ color: 'red', message: xato(e, t('common.error')) });

  const bogla = useMutation({
    mutationFn: (personId: string) => api.patch<{ tiklandi?: number }>(`/faceid/people/${personId}`, { userId: user.id }),
    onSuccess: (r) => {
      const n = r.data?.tiklandi ?? 0;
      notifications.show({ color: 'green', message: n ? t('faceLink.linkedRestored', { count: n }) : t('faceLink.linked') });
      yangila();
      onDone();
    },
    onError: xatoBildir,
  });
  const uz = useMutation({
    mutationFn: () => api.patch(`/faceid/people/${link!.personId}`, { userId: null }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('faceLink.unlinked') });
      yangila();
      onDone();
    },
    onError: xatoBildir,
  });
  const yoz = useMutation({
    mutationFn: () => api.post<{ employeeNo: string }>(`/faceid/enroll/${user.id}`),
    onSuccess: (r) => {
      notifications.show({ color: 'green', message: t('faceLink.enrollQueued', { no: r.data.employeeNo }) });
      yangila();
      qc.invalidateQueries({ queryKey: ['faceid-tasks'] });
      onDone();
    },
    onError: xatoBildir,
  });

  /* Qurilmadagi ism xodim ismiga o'xshasa — ro'yxat tepasiga chiqariladi
     (FACE ID sahifasidagi "taklif" bilan bir xil g'oya). */
  const sozlar = user.fullName.toLowerCase().split(/\s+/).filter((s) => s.length > 2);
  const oxshash = (p: UnlinkedPerson) => sozlar.some((s) => p.name.toLowerCase().includes(s));
  const variantlar = [...bosh]
    .sort((a, b) => Number(oxshash(b)) - Number(oxshash(a)))
    .map((p) => ({
      value: p.id,
      label: `№${p.employeeNo} — ${p.name.trim() || '—'}${p.hasFace ? '' : ` (${t('faceid.faceNo')})`}${oxshash(p) ? ' ★' : ''}`,
    }));

  return (
    <Stack>
      <Text fw={600}>{user.fullName}</Text>

      {link ? (
        <>
          <Group gap="xs">
            <Badge color={link.synced ? 'teal' : 'orange'} variant="light" ff="monospace" size="lg">№{link.employeeNo}</Badge>
            <Text size="sm">{link.name}</Text>
          </Group>
          <Text size="sm" c="dimmed">
            {link.synced ? (link.hasFace ? t('faceLink.okHint') : t('faceLink.noFaceHint')) : t('faceLink.pendingHint')}
          </Text>
          <Group justify="flex-end">
            <Button variant="light" color="red" leftSection={<IconLinkOff size={16} />} loading={uz.isPending} onClick={() => uz.mutate()}>
              {t('faceLink.unlink')}
            </Button>
          </Group>
        </>
      ) : (
        <>
          <Text size="sm" c="dimmed">{t('faceLink.pickHint')}</Text>
          <Select
            searchable
            placeholder={isLoading ? t('common.loading') : t('faceLink.pickPlaceholder')}
            nothingFoundMessage={t('faceLink.noFree')}
            data={variantlar}
            value={tanlov}
            onChange={setTanlov}
          />
          <Group justify="flex-end">
            <Button leftSection={<IconLink size={16} />} disabled={!tanlov} loading={bogla.isPending} onClick={() => tanlov && bogla.mutate(tanlov)}>
              {t('faceLink.link')}
            </Button>
          </Group>

          <Divider label={t('faceLink.or')} />

          <Text size="sm" c="dimmed">{t('faceLink.enrollHint')}</Text>
          <Group justify="flex-end">
            <Button variant="light" leftSection={<IconUserPlus size={16} />} loading={yoz.isPending} disabled={user.isActive === false} onClick={() => yoz.mutate()}>
              {t('faceLink.enroll')}
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );
}
